import React, { useState, useRef, useCallback } from 'react';
import { KeyRound, Copy, Check, RefreshCw, Download, QrCode, ShieldCheck, Eye, EyeOff, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { QRCodeCanvas } from 'qrcode.react';

const COMMON_PASSWORDS = [
  'password', '123456', '12345678', 'qwerty', 'abc123', 'monkey', 'master',
  'dragon', 'letmein', 'login', 'princess', 'football', 'shadow', 'sunshine',
  'trustno1', 'iloveyou', 'batman', 'access', 'hello', 'charlie', 'welcome',
  '123456789', '1234567890', 'password1', 'admin', 'passw0rd',
];

const analysePassword = (pw) => {
  if (!pw) return { score: 0, label: '', color: '', width: '0%', tips: [] };

  let score = 0;
  const tips = [];

  const hasUpper = /[A-Z]/.test(pw);
  const hasLower = /[a-z]/.test(pw);
  const hasDigit = /[0-9]/.test(pw);
  const hasSymbol = /[^A-Za-z0-9]/.test(pw);
  const isCommon = COMMON_PASSWORDS.includes(pw.toLowerCase());
  const hasRepeats = /(.)\1{2,}/.test(pw);
  const hasSequential = /(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)/i.test(pw);

  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (pw.length >= 16) score++;
  if (hasUpper) score++;
  if (hasLower) score++;
  if (hasDigit) score++;
  if (hasSymbol) score++;
  if (isCommon) score = Math.max(0, score - 4);
  if (hasRepeats) score = Math.max(0, score - 1);

  if (pw.length < 8) tips.push({ text: 'Use at least 8 characters', critical: true });
  else if (pw.length < 12) tips.push({ text: 'Consider making it 12+ characters for better security', critical: false });
  if (!hasUpper) tips.push({ text: 'Add uppercase letters (A-Z)', critical: pw.length < 12 });
  if (!hasLower) tips.push({ text: 'Add lowercase letters (a-z)', critical: pw.length < 12 });
  if (!hasDigit) tips.push({ text: 'Add numbers (0-9)', critical: false });
  if (!hasSymbol) tips.push({ text: 'Add symbols (!@#$%^&*)', critical: false });
  if (isCommon) tips.push({ text: 'This is a commonly used password — change it immediately', critical: true });
  if (hasRepeats) tips.push({ text: 'Avoid repeated characters (e.g. aaa, 111)', critical: false });
  if (hasSequential) tips.push({ text: 'Avoid sequential patterns (e.g. abc, 123)', critical: false });
  if (tips.length === 0) tips.push({ text: 'Great password! No improvements needed.', critical: false });

  if (score <= 2) return { score, label: 'Weak', color: '#ef4444', width: '25%', tips };
  if (score <= 4) return { score, label: 'Fair', color: '#eab308', width: '50%', tips };
  if (score <= 5) return { score, label: 'Strong', color: '#22c55e', width: '75%', tips };
  return { score, label: 'Very Strong', color: '#16a34a', width: '100%', tips };
};

const PasswordGenerator = () => {
  const [activeTab, setActiveTab] = useState('generate');

  // Generator state
  const [password, setPassword] = useState('');
  const [length, setLength] = useState(16);
  const [uppercase, setUppercase] = useState(true);
  const [lowercase, setLowercase] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [symbols, setSymbols] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const qrRef = useRef(null);

  // Checker state
  const [checkInput, setCheckInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const generate = useCallback(() => {
    let chars = '';
    if (uppercase) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (lowercase) chars += 'abcdefghijklmnopqrstuvwxyz';
    if (numbers) chars += '0123456789';
    if (symbols) chars += '!@#$%^&*()_+-=[]{}|;:,.<>?';
    if (!chars) chars = 'abcdefghijklmnopqrstuvwxyz';
    const arr = new Uint32Array(length);
    window.crypto.getRandomValues(arr);
    const pw = Array.from(arr, (v) => chars[v % chars.length]).join('');
    setPassword(pw);
    setCopied(false);
    setShowQR(false);
  }, [length, uppercase, lowercase, numbers, symbols]);

  const copyToClipboard = async () => {
    if (!password) return;
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = 'password-qr.png';
    a.click();
  };

  const genStrength = analysePassword(password);
  const checkStrength = analysePassword(checkInput);

  return (
    <section className="py-20 bg-slate-50" data-testid="password-generator-section">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/10 border border-blue-200 rounded-full text-blue-600 text-sm font-medium mb-4">
            <KeyRound className="w-4 h-4" />
            Free Tools
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Password Tools</h2>
          <p className="text-base text-slate-600">Generate a strong password or check how secure yours is.</p>
        </div>

        {/* Tab switcher */}
        <div className="flex rounded-xl bg-slate-200 p-1 mb-6" data-testid="password-tools-tabs">
          <button
            onClick={() => setActiveTab('generate')}
            data-testid="tab-generate"
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${activeTab === 'generate' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <RefreshCw className="w-4 h-4" /> Generate
          </button>
          <button
            onClick={() => setActiveTab('check')}
            data-testid="tab-check-strength"
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${activeTab === 'check' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <ShieldCheck className="w-4 h-4" /> Check Strength
          </button>
        </div>

        <Card className="border-2 border-slate-200 shadow-lg">
          <CardContent className="p-6 sm:p-8">

            {/* ===== GENERATE TAB ===== */}
            {activeTab === 'generate' && (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <div
                    className="flex-1 bg-slate-900 rounded-lg px-4 py-3.5 font-mono text-base text-green-400 overflow-x-auto whitespace-nowrap select-all"
                    data-testid="password-output"
                  >
                    {password || 'Click generate to create a password'}
                  </div>
                  <Button
                    onClick={copyToClipboard}
                    disabled={!password}
                    data-testid="copy-password-btn"
                    className="bg-slate-800 hover:bg-slate-700 text-white p-3 h-auto flex-shrink-0"
                    title="Copy"
                  >
                    {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                  </Button>
                </div>

                {password && (
                  <div className="mb-6">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-500">Strength</span>
                      <span style={{ color: genStrength.color }} className="font-semibold">{genStrength.label}</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: genStrength.width, backgroundColor: genStrength.color }} />
                    </div>
                  </div>
                )}

                <div className="mb-5">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-700 font-medium">Length</span>
                    <span className="text-slate-900 font-bold bg-slate-100 px-2.5 py-0.5 rounded-md text-xs">{length}</span>
                  </div>
                  <input
                    type="range" min="8" max="64" value={length}
                    onChange={(e) => setLength(Number(e.target.value))}
                    data-testid="password-length-slider"
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>8</span><span>64</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { label: 'Uppercase (A-Z)', state: uppercase, setter: setUppercase, tid: 'toggle-upper' },
                    { label: 'Lowercase (a-z)', state: lowercase, setter: setLowercase, tid: 'toggle-lower' },
                    { label: 'Numbers (0-9)', state: numbers, setter: setNumbers, tid: 'toggle-numbers' },
                    { label: 'Symbols (!@#$)', state: symbols, setter: setSymbols, tid: 'toggle-symbols' },
                  ].map((opt) => (
                    <label
                      key={opt.tid}
                      className={`flex items-center gap-2.5 p-3 rounded-lg cursor-pointer transition-colors border ${opt.state ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200'}`}
                    >
                      <input
                        type="checkbox" checked={opt.state}
                        onChange={() => opt.setter(!opt.state)}
                        data-testid={opt.tid}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={generate}
                    data-testid="generate-password-btn"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-5 rounded-lg shadow-lg shadow-blue-600/20"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Generate Password
                  </Button>
                  {password && (
                    <Button
                      onClick={() => setShowQR(!showQR)}
                      data-testid="show-qr-btn"
                      className="bg-slate-800 hover:bg-slate-700 text-white font-semibold py-5 px-5 rounded-lg"
                      title="QR Code"
                    >
                      <QrCode className="w-5 h-5" />
                    </Button>
                  )}
                </div>

                {showQR && password && (
                  <div className="mt-6 text-center" data-testid="qr-code-section">
                    <div className="inline-block bg-white border border-slate-200 rounded-xl p-5 shadow-sm" ref={qrRef}>
                      <QRCodeCanvas value={password} size={180} level="H" includeMargin />
                    </div>
                    <p className="text-xs text-slate-500 mt-3 mb-3">Scan this QR code to retrieve the password on another device</p>
                    <Button
                      onClick={downloadQR}
                      data-testid="download-qr-btn"
                      className="bg-slate-800 hover:bg-slate-700 text-white font-semibold px-6 py-3 rounded-lg text-sm"
                    >
                      <Download className="w-4 h-4 mr-2" /> Download QR Code
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* ===== CHECK STRENGTH TAB ===== */}
            {activeTab === 'check' && (
              <>
                <div className="relative mb-5">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={checkInput}
                    onChange={(e) => setCheckInput(e.target.value)}
                    placeholder="Paste or type your password here"
                    data-testid="check-password-input"
                    className="w-full bg-slate-900 rounded-lg px-4 py-3.5 pr-12 font-mono text-base text-green-400 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500 border-0"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {checkInput && (
                  <>
                    {/* Score visual */}
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-slate-500">Strength Score</span>
                        <span
                          style={{ color: checkStrength.color }}
                          className="text-lg font-bold"
                          data-testid="strength-label"
                        >
                          {checkStrength.label}
                        </span>
                      </div>
                      <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: checkStrength.width, backgroundColor: checkStrength.color }}
                          data-testid="strength-bar"
                        />
                      </div>
                    </div>

                    {/* Tips */}
                    <div className="space-y-2.5" data-testid="strength-tips">
                      {checkStrength.tips.map((tip, i) => (
                        <div
                          key={i}
                          className={`flex items-start gap-3 p-3 rounded-lg border ${tip.critical ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}
                        >
                          {tip.critical
                            ? <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                            : <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />}
                          <span className={`text-sm ${tip.critical ? 'text-red-700' : 'text-emerald-700'}`}>{tip.text}</span>
                        </div>
                      ))}
                    </div>

                    <p className="text-xs text-slate-400 mt-5 text-center">
                      Your password is checked entirely in your browser. Nothing is sent to any server.
                    </p>
                  </>
                )}

                {!checkInput && (
                  <div className="text-center py-8">
                    <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">Type or paste a password above to check its strength</p>
                    <p className="text-xs text-slate-400 mt-1">100% private — nothing leaves your browser</p>
                  </div>
                )}
              </>
            )}

          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default PasswordGenerator;
